import {
  getSingleStaticSegmentList,
  getSingleDynamicSegmentList,
  getSingleMarketoSegmentList,
  getSingleMarketoSegmentCount,
  getSinglePardotSegmentList,
  getSinglePardotSegmentCount,
  getSingleHubspotSegmentList,
  getSingleHubspotSegmentCount,
} from 'api/contacts'
import { getNewSegmentList } from 'api/segments';
import { shouldShowMarketoImports } from 'utils/marketoAccess';
import { shouldShowPardotImports } from 'utils/pardotAccess';
import { shouldShowHubspotImports } from 'utils/hubspotAccess';

const ID_LIST = {
  key: 'ID_LIST',
  displayName: 'Static',
  displayNameFull: 'Static segment',
  displayNameFullPlural: 'Static segments',
  fetchSegmentMembers: ({ segment, pageSize, pageIndex }) => {
    return getSingleStaticSegmentList({
      id: segment.id,
      size: pageSize,
      startFrom: pageIndex
    })
  },
  getSegmentMembersFromFetchData: ({ data }) => data.results,
  getTotalNumberOfContactsFromFetchData: ({ data }) => data.totalMatching
}

const QUERY = {
  key: 'QUERY',
  displayName: 'Dynamic',
  displayNameFull: 'Dynamic segment',
  displayNameFullPlural: 'Dynamic segments',
  fetchSegmentMembers: ({ segment, pageSize, pageIndex, orgId }) => {
    return getSingleDynamicSegmentList({
      id: orgId,
      query: segment.query,
      size: pageSize,
      startFrom: pageIndex
    })
  },
  getSegmentMembersFromFetchData: ({ data }) => data.results,
  getTotalNumberOfContactsFromFetchData: ({ data }) => data.totalMatching
}

const STATIC = {
  key: 'STATIC',
  displayName: 'Static',
  displayNameFull: 'Static segment',
  displayNameFullPlural: 'Static segments',
  fetchSegmentMembers: ({ segment, pageSize, pageIndex }) => {
    return getNewSegmentList({
      id: segment.id,
      size: pageSize,
      startFrom: pageIndex
    })
  },
  getSegmentMembersFromFetchData: ({ data }) => data.hydratedSegmentResults,
  getTotalNumberOfContactsFromFetchData: ({ data }) => data.total
}

const DYNAMIC = {
  key: 'DYNAMIC',
  displayName: 'Dynamic',
  displayNameFull: 'Dynamic segment',
  displayNameFullPlural: 'Dynamic segments',
  fetchSegmentMembers: ({ segment, pageSize, pageIndex }) => {
    return getNewSegmentList({
      id: segment.id,
      size: pageSize,
      startFrom: pageIndex
    })
    .then(( response ) => response)
    .catch(error => {
      if (error.code === 'ECONNABORTED') {
        return {
          data: {
            hydratedSegmentResults: [],
            total: 0,
            timedOut: true
          }
        }
      }
    })
  },
  getSegmentMembersFromFetchData: ({ data }) => data.hydratedSegmentResults,
  getTotalNumberOfContactsFromFetchData: ({ data }) => data.total
}

const MARKETO = {
  key: 'MARKETO',
  displayName: 'Marketo',
  displayNameFull: 'Marketo segment',
  displayNameFullPlural: 'Marketo segments',
  fetchSegmentMembers: ({ segment, pageSize, pageIndex }) => {
    return getSingleMarketoSegmentList({
      id: segment.id,
      size: pageSize,
      startFrom: pageIndex
    })
  },
  getSegmentMembersFromFetchData: ({ data }) => data.results,
  getTotalNumberOfContactsFromFetchData: async ({ segment }) => {
    let totalCountRes = await getSingleMarketoSegmentCount({ id: segment.id })
    return totalCountRes.data
  }
}

const PARDOT = {
  key: 'PARDOT',
  displayName: 'Pardot',
  displayNameFull: 'Pardot segment',
  displayNameFullPlural: 'Pardot segments',
  fetchSegmentMembers: ({ segment, pageSize, pageIndex }) => {
    return getSinglePardotSegmentList({
      id: segment.id,
      size: pageSize,
      startFrom: pageIndex
    })
  },
  getSegmentMembersFromFetchData: ({ data }) => data.results,
  getTotalNumberOfContactsFromFetchData: async ({ segment }) => {
    let totalCountRes = await getSinglePardotSegmentCount({ id: segment.id })
    return totalCountRes.data
  }
}

const HUBSPOT = {
  key: 'HUBSPOT',
  displayName: 'HubSpot',
  displayNameFull: 'HubSpot segment',
  displayNameFullPlural: 'HubSpot segments',
  fetchSegmentMembers: ({ segment, pageSize, pageIndex }) => {
    return getSingleHubspotSegmentList({
      id: segment.id,
      size: pageSize,
      startFrom: pageIndex
    })
  },
  getSegmentMembersFromFetchData: ({ data }) => data.results,
  getTotalNumberOfContactsFromFetchData: async ({ segment }) => {
    let totalCountRes = await getSingleHubspotSegmentCount({ id: segment.id })
    return totalCountRes.data
  }
}

const SegmentTypes = {
  STATIC,
  ID_LIST,
  DYNAMIC,
  QUERY,
  MARKETO,
  PARDOT,
  HUBSPOT
}

export const getSegmentTypesAvailableForFiltering = () => {
  const types = [DYNAMIC, STATIC]

  if (shouldShowMarketoImports()) {
    types.push(MARKETO)
  }

  if (shouldShowPardotImports()) {
    types.push(PARDOT)
  }

  if (shouldShowHubspotImports()) {
    types.push(HUBSPOT)
  }

  return types
}

export const getSegmentTypeDisplayName = (segmentKey) => {
  const segmentType = SegmentTypes[segmentKey]

  if (!segmentType) {
    console.error(`Could not find segment type ${segmentKey}`)
    return ''
  }

  return segmentType.displayName
}

export default SegmentTypes
